declare var google: any;

export interface ClassifiedPolygon {
    outerRing: google.maps.LatLngLiteral[];
    holes: google.maps.LatLngLiteral[][];
    multiParts: google.maps.LatLngLiteral[][];
    isMultiPolygon: boolean;
    hasHoles: boolean;
}

export interface EditedPolygonResult {
    outerRingEncoded: string;
    holesEncoded: string[];
    multiPartsEncoded: string[];
    isMultiPolygon: boolean;
    hasHoles: boolean;
}

export interface PolygonPayload {
    polygon: string | string[];
    interiorPolygons: string[] | null;
}

export function classifyPolygon(polygon: any[][][]): ClassifiedPolygon {
    const result: ClassifiedPolygon = {
        outerRing: [],
        holes: [],
        multiParts: [],
        isMultiPolygon: false,
        hasHoles: false,
    };

    if (!polygon || polygon.length === 0) return result;

    const group = polygon[0];

    if (!Array.isArray(group) || group.length === 0) return result;
    const firstElement = group[0];

    if (firstElement && typeof firstElement === 'object' && 'lat' in firstElement) {
        result.outerRing = group as unknown as google.maps.LatLngLiteral[];
        return result;
    }
    const rings = group as unknown as google.maps.LatLngLiteral[][];

    if (rings.length === 0) return result;

    result.outerRing = rings[0];

    if (rings.length === 1) return result;
    const outerGooglePoly = new google.maps.Polygon({
        paths: rings[0].map((p) => new google.maps.LatLng(p.lat, p.lng)),
    });

    for (let i = 1; i < rings.length; i++) {
        const ring = rings[i];
        if (!ring || ring.length === 0) continue;

        const latLngs = ring.map((p) => new google.maps.LatLng(p.lat, p.lng));
        const sampleSize = Math.min(latLngs.length, 8);
        const step = Math.max(1, Math.floor(latLngs.length / sampleSize));
        let insideCount = 0;
        let sampledCount = 0;

        for (let j = 0; j < latLngs.length; j += step) {
            if (google.maps.geometry.poly.containsLocation(latLngs[j], outerGooglePoly)) {
                insideCount++;
            }
            sampledCount++;
        }

        const insideRatio = insideCount / sampledCount;
        if (insideRatio > 0.5) {
            result.holes.push(ring);
        } else {
            result.multiParts.push(ring);
        }
    }

    result.hasHoles = result.holes.length > 0;
    result.isMultiPolygon = result.multiParts.length > 0;

    return result;
}

export function classifyEditedPolygon(
    editablePolygon: google.maps.Polygon
): EditedPolygonResult {
    const result: EditedPolygonResult = {
        outerRingEncoded: '',
        holesEncoded: [],
        multiPartsEncoded: [],
        isMultiPolygon: false,
        hasHoles: false,
    };

    const paths = editablePolygon.getPaths();
    if (paths.getLength() === 0) return result;

    const outerPath = paths.getAt(0);
    result.outerRingEncoded = google.maps.geometry.encoding.encodePath(outerPath);

    if (paths.getLength() === 1) return result;

    const outerPoly = new google.maps.Polygon({ paths: outerPath });

    for (let i = 1; i < paths.getLength(); i++) {
        const path = paths.getAt(i);
        const points = path.getArray();
        const encoded = google.maps.geometry.encoding.encodePath(path);

        const sampleSize = Math.min(points.length, 8);
        const step = Math.max(1, Math.floor(points.length / sampleSize));
        let insideCount = 0;
        let sampledCount = 0;

        for (let j = 0; j < points.length; j += step) {
            if (google.maps.geometry.poly.containsLocation(points[j], outerPoly)) {
                insideCount++;
            }
            sampledCount++;
        }

        if (insideCount / sampledCount > 0.5) {
            result.holesEncoded.push(encoded);
        } else {
            result.multiPartsEncoded.push(encoded);
        }
    }

    result.hasHoles = result.holesEncoded.length > 0;
    result.isMultiPolygon = result.multiPartsEncoded.length > 0;

    return result;
}

export function buildPolygonPayload(edited: EditedPolygonResult, preservedMultiParts: string[] = []): PolygonPayload {
    const allOuterParts = [edited.outerRingEncoded, ...edited.multiPartsEncoded, ...preservedMultiParts];

    return {
        polygon: allOuterParts.length === 1 ? allOuterParts[0] : allOuterParts,
        interiorPolygons: edited.holesEncoded.length > 0 ? edited.holesEncoded : null,
    };
}

export function isPointInsideField(
    point: google.maps.LatLngLiteral,
    polygon: any[][][]
): boolean {
    if (!polygon || polygon.length === 0) return false;

    const classified = classifyPolygon(polygon);
    const latLng = new google.maps.LatLng(point.lat, point.lng);
    const EPSILON = 0.000001;

    const outerPoly = new google.maps.Polygon({
        paths: classified.outerRing.map((p) => new google.maps.LatLng(p.lat, p.lng)),
    });

    if (google.maps.geometry.poly.containsLocation(latLng, outerPoly)) {
        return true;
    }

    for (const bp of classified.outerRing) {
        if (Math.abs(latLng.lat() - bp.lat) < EPSILON && Math.abs(latLng.lng() - bp.lng) < EPSILON) {
            return true;
        }
    }

    for (const part of classified.multiParts) {
        const partPoly = new google.maps.Polygon({
            paths: part.map((p) => new google.maps.LatLng(p.lat, p.lng)),
        });
        if (google.maps.geometry.poly.containsLocation(latLng, partPoly)) {
            return true;
        }
        for (const bp of part) {
            if (Math.abs(latLng.lat() - bp.lat) < EPSILON && Math.abs(latLng.lng() - bp.lng) < EPSILON) {
                return true;
            }
        }
    }

    return false;
}